import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, Menu, X, Home, MessageSquare, Newspaper, FileText, Book, Info } from 'lucide-react';

const Navigation = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Determine if the current path is active
  const isActive = (path) => {
    if (path === '/q' || path === '/q/') {
      // Make Claims active for /q, /q/ and any paths starting with /q/
      return location.pathname === '/q' || location.pathname === '/q/' || location.pathname.startsWith('/q/') 
        ? 'text-slate-900' 
        : 'text-gray-500 hover:text-gray-900';
    } else if (path === '/a' || path === '/a/') {
      // Make Articles active for /a, /a/, /articles, and any paths starting with /a/
      return location.pathname === '/a' || location.pathname === '/a/' || 
             location.pathname === '/articles' || location.pathname === '/articles/' || 
             location.pathname.startsWith('/a/') 
        ? 'text-slate-900' 
        : 'text-gray-500 hover:text-gray-900';
    } else if (path === '/p' || path === '/p/') {
      // Make Policies active for /p, /p/, /policies, and any paths starting with /p/
      return location.pathname === '/p' || location.pathname === '/p/' || 
             location.pathname === '/policies' || location.pathname === '/policies/' || 
             location.pathname.startsWith('/p/') 
        ? 'text-slate-900' 
        : 'text-gray-500 hover:text-gray-900';
    } else if (path === '/o' || path === '/o/') {
      // Make Opinions active for /o, /o/, /opinions, and any paths starting with /o/
      return location.pathname === '/o' || location.pathname === '/o/' || 
             location.pathname === '/opinions' || location.pathname === '/opinions/' || 
             location.pathname.startsWith('/o/') 
        ? 'text-slate-900' 
        : 'text-gray-500 hover:text-gray-900';
    } else {
      // Normal exact match for other paths
      return location.pathname === path 
        ? 'text-slate-900' 
        : 'text-gray-500 hover:text-gray-900';
    }
  };

  const isActiveBottom = (path) => {
    if (path === '/q' || path === '/q/') {
      return location.pathname === '/q' || location.pathname === '/q/' || location.pathname.startsWith('/q/') 
        ? 'active' 
        : '';
    } else if (path === '/a' || path === '/a/') {
      return location.pathname === '/a' || location.pathname === '/a/' || 
             location.pathname === '/articles' || location.pathname === '/articles/' || 
             location.pathname.startsWith('/a/') 
        ? 'active' 
        : '';
    } else if (path === '/p' || path === '/p/') {
      return location.pathname === '/p' || location.pathname === '/p/' || 
             location.pathname === '/policies' || location.pathname === '/policies/' || 
             location.pathname.startsWith('/p/') 
        ? 'active' 
        : '';
    } else if (path === '/o' || path === '/o/') {
      return location.pathname === '/o' || location.pathname === '/o/' || 
             location.pathname === '/opinions' || location.pathname === '/opinions/' || 
             location.pathname.startsWith('/o/') 
        ? 'active' 
        : '';
    } else {
      return location.pathname === path ? 'active' : '';
    }
  };

  return (
    <>
      {/* Top navigation for all screen sizes */}
      <nav className="bg-white shadow-sm fixed w-full top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-14">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center pl-1">
                <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-white text-[10px] font-medium px-1">
                  CRH
                </div>
                <span className="ml-2 font-medium text-slate-800 hidden sm:block">Cult of the Red Hat</span>
              </div>
            </div>
            
            {/* Desktop navigation - hidden on mobile */}
            <div className="hidden sm:flex items-center space-x-4">
              <Link to="/" className={`${isActive('/')} px-2 py-1 text-sm font-medium`}>
                Home
              </Link>
              <Link to="/q/" className={`${isActive('/q')} px-2 py-1 text-sm font-medium`}>
                Claims
              </Link>
              <Link to="/a/" className={`${isActive('/a')} px-2 py-1 text-sm font-medium`}>
                Articles
              </Link>
              <Link to="/p/" className={`${isActive('/p')} px-2 py-1 text-sm font-medium`}>
                Policies
              </Link>
              <Link to="/o/" className={`${isActive('/o')} px-2 py-1 text-sm font-medium`}>
                Opinions
              </Link>
              <Link to="/about.html" className={`${isActive('/about.html')} px-2 py-1 text-sm font-medium`}>
                About
              </Link>
              <button className="bg-slate-100 p-1 rounded-full text-gray-500 hover:text-gray-800 mobile-touch-target">
                <Search size={16} />
              </button>
            </div>
            
            {/* Mobile controls */}
            <div className="flex items-center space-x-3 sm:hidden">
              <button 
                className="bg-slate-100 p-1 rounded-full text-gray-500 hover:text-gray-800 mobile-touch-target"
                aria-label="Search"
              >
                <Search size={20} />
              </button>
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="mobile-touch-target p-1 text-gray-500 hover:text-gray-800"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile menu drawer */}
        {mobileMenuOpen && (
          <div className="sm:hidden bg-white shadow-lg border-t border-gray-100 absolute w-full">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link 
                to="/" 
                className={`${isActive('/') === 'text-slate-900' ? 'bg-slate-100' : ''} block px-3 py-3 rounded-md text-base font-medium`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/q/" 
                className={`${isActive('/q') === 'text-slate-900' ? 'bg-slate-100' : ''} block px-3 py-3 rounded-md text-base font-medium`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Claims
              </Link>
              <Link 
                to="/a/" 
                className={`${isActive('/a') === 'text-slate-900' ? 'bg-slate-100' : ''} block px-3 py-3 rounded-md text-base font-medium`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Articles
              </Link>
              <Link 
                to="/p/" 
                className={`${isActive('/p') === 'text-slate-900' ? 'bg-slate-100' : ''} block px-3 py-3 rounded-md text-base font-medium`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Policies
              </Link>
              <Link 
                to="/o/" 
                className={`${isActive('/o') === 'text-slate-900' ? 'bg-slate-100' : ''} block px-3 py-3 rounded-md text-base font-medium`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Opinions
              </Link>
              <Link 
                to="/about.html" 
                className={`${isActive('/about.html') === 'text-slate-900' ? 'bg-slate-100' : ''} block px-3 py-3 rounded-md text-base font-medium`}
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navigation;